import { useState, useEffect, useMemo, useRef } from 'react';
import { Col, Flex, Form, Row } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import {
  DayOfWeek,
  RepeatUnit,
  type TimeOfDay,
} from '@/proto/logiblocks/logiblocks';
import type { CustomizationComponent } from '@/pages/Logiblocks/types';
import { DayOfWeekPicker } from '../DayOfWeekPicker';
import MonthPicker from './components/MonthPicker';
import { FieldErrorLabel, RequiredAsterisk } from '../shared';
import {
  DEFAULT_SCHEDULE_TIME,
  ScheduleTriggerV2Fields,
} from './ScheduleTriggerV2NodeInfo';
import { getUserTimeZoneValue } from '@/common/utils/utils';
import {
  CustomSelect,
  CustomShipmentText,
  CustomTimePicker,
} from '@/components';
import { useToken } from '@/common/helper';
import { formatTimezoneOffset } from '@/pages/Logiblocks/dateTimeUtils';

/** Recurrence-unit options for the "Repeat every" dropdown. */
const RepeatEveryList = [
  { value: RepeatUnit.DAY, label: 'Day' },
  { value: RepeatUnit.WEEK, label: 'Week' },
  { value: RepeatUnit.MONTH, label: 'Month' },
];

/** Options for how many times a day the schedule runs. */
const FrequencyOptions: { value: number; label: string }[] = [
  { value: 1, label: 'Once a day' },
  { value: 2, label: 'Twice a day' },
  { value: 3, label: 'Three times a day' },
];

dayjs.extend(utc);
dayjs.extend(timezone);

export const ScheduleTriggerV2Customization: CustomizationComponent = ({
  nodeData,
  onChange,
  isReadOnly = false,
  isSidePannelOpen,
  validationErrors,
}) => {
  const repeatIntervalError = validationErrors?.find(
    (e) => e.field === ScheduleTriggerV2Fields.REPEAT_INTERVAL
  );
  const repeatEveryError = validationErrors?.find(
    (e) => e.field === ScheduleTriggerV2Fields.REPEAT_EVERY
  );
  const frequencyError = validationErrors?.find(
    (e) => e.field === ScheduleTriggerV2Fields.FREQUENCY
  );
  const daysOfWeekError = validationErrors?.find(
    (e) => e.field === ScheduleTriggerV2Fields.DAYS_OF_WEEK
  );
  const daysOfMonthError = validationErrors?.find(
    (e) => e.field === ScheduleTriggerV2Fields.DAYS_OF_MONTH
  );
  const [form] = Form.useForm();
  //options list for interval dropdown
  const repeatOptions = Array.from({ length: 6 }, (_, i) => ({
    label: `${i + 1}`,
    value: i + 1,
  }));

  const { decodedUserToken } = useToken();
  const decodedToken = decodedUserToken();
  const userTimeZone = getUserTimeZoneValue(decodedToken);
  // An existing schedule keeps the timezone it was created in; a brand-new node
  // (no saved timezone) adopts the editor's zone.
  const [scheduleTimeZone, setScheduleTimeZone] = useState(
    nodeData.schedule_trigger_v2?.timezone || ''
  );
  const effectiveTimeZone = scheduleTimeZone || userTimeZone;
  const timezoneLabel = useMemo(() => {
    const offsetMinutes = dayjs().tz(effectiveTimeZone).utcOffset();
    return `Timezone set to ${offsetMinutes ? `(${formatTimezoneOffset(offsetMinutes)})` : ''} ${effectiveTimeZone}`;
  }, [effectiveTimeZone]);

  const formValues = Form.useWatch([], form);
  const [daysOfWeek, setDaysOfWeek] = useState<DayOfWeek[]>([]);
  const [daysOfMonth, setDaysOfMonth] = useState<number[]>([]);
  const handleDaysChange = (days: DayOfWeek[]) => setDaysOfWeek(days);
  const handleMonthDaysChange = (days: number[]) => setDaysOfMonth(days);
  // Re-emit the node data whenever a form field or day picker changes.
  useEffect(() => {
    // Skip before the form has loaded so we don't overwrite saved data with an
    // empty object that lacks the required interval and unit fields.
    if (formValues === undefined) return;
    const rawTimes: (Dayjs | null)[] = formValues.scheduleTimes || [];
    const scheduleTimes: TimeOfDay[] = rawTimes
      .filter((t): t is Dayjs => Boolean(t))
      .map((t) => ({ hour: t.hour(), minute: t.minute() }));
    const scheduleTriggerV2 = {
      // Coerce to 0/UNSPECIFIED when cleared so the proto encoder never sees undefined.
      repeat_interval: formValues.repeatInterval ?? 0,
      repeat_every: formValues.repeatEvery ?? RepeatUnit.UNSPECIFIED,
      schedule_times: scheduleTimes,
      days_of_week: daysOfWeek,
      days_of_month: daysOfMonth,
      timezone: effectiveTimeZone,
    };
    onChange({ schedule_trigger_v2: scheduleTriggerV2 });
  }, [formValues, daysOfWeek, daysOfMonth, onChange, effectiveTimeZone]);

  const hasPatchedRef = useRef(false);

  useEffect(() => {
    if (!isSidePannelOpen || hasPatchedRef.current) return;
    if (!nodeData.schedule_trigger_v2) return;
    hasPatchedRef.current = true;
    const {
      repeat_interval: repeatInterval,
      repeat_every: repeatEvery,
      schedule_times: scheduleTimes,
      days_of_week: savedDays,
      days_of_month: savedMonthDays,
      timezone: savedTimeZone,
    } = nodeData.schedule_trigger_v2;
    const parsedTimes = (scheduleTimes || []).map((t) =>
      dayjs().hour(t.hour).minute(t.minute)
    );

    form.setFieldsValue({
      repeatInterval,
      repeatEvery: repeatEvery || undefined,
      // Show one "Run at" picker for each saved run time.
      frequency: parsedTimes.length || undefined,
      scheduleTimes: parsedTimes,
    });

    if (savedDays?.length) setDaysOfWeek(savedDays);
    if (savedMonthDays?.length) setDaysOfMonth(savedMonthDays);
    if (savedTimeZone) {
      setScheduleTimeZone(savedTimeZone);
    }
  }, [isSidePannelOpen, nodeData, form]);

  useEffect(() => {
    if (!isSidePannelOpen) {
      hasPatchedRef.current = false;
    }
  }, [isSidePannelOpen]);

  return (
    <>
      <Form form={form} layout='vertical' disabled={isReadOnly}>
        <Flex vertical gap={16}>
          <Flex vertical gap={4}>
            <Flex align='center' gap={4}>
              <CustomShipmentText
                text={'Repeat every'}
                fontSize='14px'
                fontWeight={600}
              />
              <RequiredAsterisk />
            </Flex>
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item
                  className='custom-input form-input-style'
                  name='repeatInterval'
                >
                  <CustomSelect
                    border='0px'
                    textColor='var(--text-secondary)'
                    lineHeight='38px'
                    options={repeatOptions}
                    placeholder='Select...'
                    allowClear
                  />
                </Form.Item>
                <FieldErrorLabel error={repeatIntervalError} />
              </Col>
              <Col span={12}>
                <Form.Item
                  className='custom-input form-input-style'
                  name='repeatEvery'
                >
                  <CustomSelect
                    border='0px'
                    textColor='var(--text-secondary)'
                    lineHeight='38px'
                    options={RepeatEveryList}
                    placeholder='Select...'
                    allowClear
                    onChange={() => {
                      setDaysOfMonth([]);
                      setDaysOfWeek([]);
                    }}
                  />
                </Form.Item>
                <FieldErrorLabel error={repeatEveryError} />
              </Col>
            </Row>
          </Flex>
          {[RepeatUnit.WEEK, RepeatUnit.MONTH].includes(
            formValues?.repeatEvery
          ) && (
            <Form.Item shouldUpdate>
              {() => {
                const repeatEvery = form.getFieldValue('repeatEvery');
                if (
                  ![RepeatUnit.WEEK, RepeatUnit.MONTH].includes(repeatEvery)
                ) {
                  return null;
                }
                return (
                  <Flex vertical gap={4}>
                    <Flex align='center' gap={4}>
                      <CustomShipmentText
                        text={
                          repeatEvery === RepeatUnit.MONTH
                            ? 'On day(s) of month'
                            : 'Repeat on'
                        }
                        fontSize='14px'
                        fontWeight={600}
                      />
                      <RequiredAsterisk />
                    </Flex>
                    {repeatEvery === RepeatUnit.WEEK && (
                      <>
                        <DayOfWeekPicker
                          selectedDays={daysOfWeek}
                          onChange={handleDaysChange}
                          disabled={isReadOnly}
                        />
                        <FieldErrorLabel error={daysOfWeekError} />
                      </>
                    )}
                    {repeatEvery === RepeatUnit.MONTH && (
                      <>
                        <MonthPicker
                          value={daysOfMonth}
                          onChange={handleMonthDaysChange}
                          disabled={isReadOnly}
                        />
                        <FieldErrorLabel error={daysOfMonthError} />
                      </>
                    )}
                  </Flex>
                );
              }}
            </Form.Item>
          )}
          <div>
            <Form.Item
              className='custom-input form-input-style'
              // Inlined instead of <FieldLabel> because Form.Item wraps its
              // `label` prop in a <label> element — nesting FieldLabel (itself
              // a <label>) would produce invalid nested-label markup.
              label={
                <>
                  Frequency
                  <RequiredAsterisk marginLeft={4} />
                </>
              }
              name='frequency'
            >
              <CustomSelect
                border='0px'
                textColor='var(--text-secondary)'
                lineHeight='38px'
                options={FrequencyOptions}
                placeholder='Select...'
              />
            </Form.Item>
            {/* Once a frequency is chosen the missing-value error moves to the
                empty time picker, so only flag the dropdown itself here. */}
            <FieldErrorLabel
              error={formValues?.frequency ? undefined : frequencyError}
            />
          </div>
          {form.getFieldValue('frequency') && (
            <Form.Item
              shouldUpdate={(prev, curr) => prev.frequency !== curr.frequency}
            >
              {({ getFieldValue, setFieldsValue }) => {
                const frequency = getFieldValue('frequency') || 0;
                const times = getFieldValue('scheduleTimes') || [];
                if (times.length !== frequency) {
                  // Added slots start at the default time so the schedule stays
                  // valid as the user raises the frequency.
                  const updated = Array.from(
                    { length: frequency },
                    (_, i) =>
                      times[i] ||
                      dayjs()
                        .hour(DEFAULT_SCHEDULE_TIME.hour)
                        .minute(DEFAULT_SCHEDULE_TIME.minute)
                  );

                  setFieldsValue({ scheduleTimes: updated });
                }

                return (
                  <>
                    <Form.List name='scheduleTimes'>
                      {(fields) => (
                        <Row gutter={12}>
                          {fields.map((field, index) => (
                            <Col span={24 / frequency} key={field.key}>
                              <Form.Item {...field} label={'Run at'}>
                                <CustomTimePicker
                                  value={
                                    form.getFieldValue('scheduleTimes')?.[index]
                                  }
                                  onChange={() => {}}
                                  timeZone={effectiveTimeZone}
                                  className='custom-picker delivered-modal-inputs'
                                  showNow={false}
                                  disabled={isReadOnly}
                                  allowClear={false}
                                />
                              </Form.Item>
                            </Col>
                          ))}
                        </Row>
                      )}
                    </Form.List>
                    <FieldErrorLabel error={frequencyError} />
                    <CustomShipmentText
                      text={timezoneLabel}
                      fontSize='10px'
                      color={'var(--text-secondary)'}
                      marginTop='4px'
                    />
                  </>
                );
              }}
            </Form.Item>
          )}
        </Flex>
      </Form>
    </>
  );
};
